import {
  overlayTemplateBase,
  resolveSponsorLogoUrl,
} from 'ui/VideoPlayer/components/StreamOverlay/streamOverlayTemplate';
import {
  MatchupCorner,
  CreateMatchupInput,
  CreateMatchupCompetitorInput,
} from 'api/graphql-global-types';
import {
  MatchupFields as Matchup,
  MatchupFields_competitors as MatchupCompetitor,
} from 'api/matchup/types/MatchupFields';
import { FightDraft, FightCompetitorDraft } from './fightCardTypes';

export const SCOREBOARD_TEMPLATE_ID = 'scoreboard-10';

export const SCOREBOARD_TEMPLATE_IDS: readonly string[] = [
  'scoreboard-10',
  'scoreboard-15',
  'scoreboard-14',
  'scoreboard-13',
  'scoreboard-11',
  'scoreboard-12',
  'scoreboard-16',
  'scoreboard-17',
];
export const SCOREBOARD_TEMPLATE_PREFIX = 'scoreboard-';

export const isScoreboardTemplate = (id?: string | null): boolean =>
  !!id && id.startsWith(SCOREBOARD_TEMPLATE_PREFIX);
export const SCOREBOARD_CARD_TEMPLATE_IDS: readonly string[] = [
  'scoreboard-11',
  'scoreboard-12',
  'scoreboard-16',
  'scoreboard-17',
];
export const isScoreboardCard = (id?: string | null): boolean =>
  !!id && SCOREBOARD_CARD_TEMPLATE_IDS.includes(id);

export const getCompetitorName = (
  competitor?: MatchupCompetitor | null
): string => {
  if (!competitor) {
    return 'TBD';
  }
  return competitor.store?.storeDetails?.storeName || competitor.name || 'TBD';
};

export const getCompetitorByCorner = (
  matchup: Matchup,
  corner: MatchupCorner
): MatchupCompetitor | undefined =>
  matchup.competitors?.find((c) => c.corner === corner);

export const getFighterAName = (matchup: Matchup): string =>
  getCompetitorName(getCompetitorByCorner(matchup, MatchupCorner.A));

export const getFighterBName = (matchup: Matchup): string =>
  getCompetitorName(getCompetitorByCorner(matchup, MatchupCorner.B));

export const getMatchupTitle = (matchup: Matchup): string =>
  `${getFighterAName(matchup)} vs ${getFighterBName(matchup)}`;

export const DEFAULT_ROUND_MINUTES = 3;

export const getRoundDurationSeconds = (matchup?: Matchup | null): number =>
  (matchup?.roundMinutes ?? DEFAULT_ROUND_MINUTES) * 60;

export const formatTimer = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(minutes)}:${pad(seconds)}`;
};

export const formatRoundLabel = (
  currentRound?: number | null,
  totalRounds?: number | null
): string => {
  const round = currentRound ?? 1;
  if (totalRounds && totalRounds > 0) {
    return `ROUND ${round} OF ${totalRounds}`;
  }
  return `ROUND ${round}`;
};

export type ScoreboardOverlayValues = {
  fighterA?: string | null;
  fighterB?: string | null;
  weightClass?: string | null;
  roundLabel?: string | null;
  timer?: string | null;
  sponsorLogoKey?: string | null;
};

export const buildScoreboardOverlaySrc = (
  values: ScoreboardOverlayValues,
  templateId: string = SCOREBOARD_TEMPLATE_ID
): string => {
  const params = new URLSearchParams();
  params.set('fighterA', values.fighterA || '');
  params.set('fighterB', values.fighterB || '');
  params.set('weightClass', values.weightClass || '');
  params.set('roundLabel', values.roundLabel || '');
  params.set('timer', values.timer || '');
  params.set('sponsorLogoUrl', resolveSponsorLogoUrl(values.sponsorLogoKey));
  const id = isScoreboardTemplate(templateId)
    ? templateId
    : SCOREBOARD_TEMPLATE_ID;
  return `${overlayTemplateBase(id)}/index.html?${params.toString()}`;
};

const competitorToDraft = (
  m: Matchup,
  corner: MatchupCorner
): FightCompetitorDraft => {
  const competitor = getCompetitorByCorner(m, corner);
  return {
    storeId: competitor?.storeId ?? null,
    name: competitor?.store?.storeDetails?.storeName || competitor?.name || '',
    avatarURL: competitor?.store?.storeDetails?.avatarURL ?? null,
    invitedEmail: null,
    inviteToken: null,
  };
};

export const matchupToDraft = (m: Matchup): FightDraft => ({
  matchupId: m.id,
  weightClass: m.category ?? '',
  fighterA: competitorToDraft(m, MatchupCorner.A),
  fighterB: competitorToDraft(m, MatchupCorner.B),
  rounds: m.rounds ?? null,
  roundMinutes: m.roundMinutes ?? null,
  isMainEvent: m.isMainEvent,
});

const competitorToInput = (
  corner: MatchupCorner,
  competitor: FightCompetitorDraft
): CreateMatchupCompetitorInput | null => {
  if (competitor.storeId) {
    return { corner, storeId: competitor.storeId };
  }
  if (!competitor.name.trim()) {
    return null;
  }
  return {
    corner,
    name: competitor.name.trim(),
    invitedEmail: competitor.invitedEmail || undefined,
    inviteToken: competitor.inviteToken || undefined,
  };
};

export const draftToMatchupInput = (
  draft: FightDraft,
  index: number
): CreateMatchupInput => ({
  ...(draft.matchupId && { id: draft.matchupId }),
  order: index,
  isMainEvent: draft.isMainEvent,
  category: draft.weightClass || null,
  rounds: draft.rounds,
  roundMinutes: draft.roundMinutes,
  competitors: [
    competitorToInput(MatchupCorner.A, draft.fighterA),
    competitorToInput(MatchupCorner.B, draft.fighterB),
  ].filter((c): c is CreateMatchupCompetitorInput => c !== null),
});
